import type React from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Heading } from '../catalyst/heading'
import { Text } from '../catalyst/text'
import { Button } from '../catalyst/button'
import { api } from './api'
import type { User } from './types'

type AuthContextValue = {
  user: User | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function initialsFor(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const initials = parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '')
  return initials.join('') || name.slice(0, 2).toUpperCase()
}

type MeResponse = { user: User }

export function AuthProvider({ children }: React.PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api
      .get<MeResponse>('/me')
      .then(({ user }) => setUser(user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false))
  }, [])

  async function signIn(email: string, password: string) {
    const { user } = await api.post<MeResponse>('/auth/login', { email, password })
    setUser(user)
  }

  async function signOut() {
    try {
      await api.post('/auth/logout')
    } catch {
      // The session may already be gone server-side; clear local state regardless.
    }
    setUser(null)
  }

  return <AuthContext.Provider value={{ user, loading, signIn, signOut }}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}

/** Signed in AND Farmula staff. Fails closed: a user the API does not flag as staff sees "no access". */
export function RequireStaff({ children }: React.PropsWithChildren) {
  const { user, loading, signOut } = useAuth()
  const location = useLocation()

  if (loading) return null
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />
  if (user.isStaff !== true) {
    return (
      <div className="mx-auto grid max-w-sm gap-4 p-10">
        <Heading>No access</Heading>
        <Text>{user.email} is not a Farmula staff account.</Text>
        <Button outline onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    )
  }
  return <>{children}</>
}
